import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Spin, Table, Tag, Tooltip } from "antd";
import { Spinner } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import { toast } from "react-toastify";
import "bootstrap-daterangepicker/daterangepicker.css";
import SidebarNav from "../../sidebar";
import CustomPagination from "../../Pagination/pagination";
import FilterPanel from "../../commoncomponent/FilterPanel";
import TableToolbar from "../../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../../hooks/useTableSettings";
import { sanitizeInput } from "../../../../config/validation";
import {
  getFundingWalletTransactionsApi,
  exportFundingWalletTransactionsCsvApi,
} from "../../../../service/api";
import { saveBlobAsFile } from "../shared";

const TYPE_OPTIONS = [
  { label: "All", value: "" },
  { label: "Credit", value: "CREDIT" },
  { label: "Debit", value: "DEBIT" },
];

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Pending", value: "PENDING" },
  { label: "On Hold", value: "ON_HOLD" },
  { label: "Failed", value: "FAILED" },
  { label: "Rejected", value: "REJECTED" },
];

const STATUS_TAG = {
  COMPLETED: { color: "green",  label: "Completed" },
  PENDING:   { color: "orange", label: "Pending"   },
  ON_HOLD:   { color: "gold",   label: "On Hold"   },
  FAILED:    { color: "red",    label: "Failed"     },
  REJECTED:  { color: "red",    label: "Rejected"   },
};

const formatDateTime = (val) =>
  val ? moment(val).format("DD MMM YYYY, HH:mm") : "-";

const formatAmount = (val) => {
  const num = Number(val);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
};

const truncateMiddle = (str = "", head = 6, tail = 4) => {
  if (!str || str.length <= head + tail + 3) return str || "-";
  return `${str.slice(0, head)}…${str.slice(-tail)}`;
};

const TxIdCell = ({ id }) => {
  const [copied, setCopied] = useState(false);
  if (!id) return <span>-</span>;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <Tooltip title={id}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>
          {truncateMiddle(id)}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          title={copied ? "Copied" : "Copy"}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 12,
            color: copied ? "#0a7a3b" : "#666",
            padding: "0 2px",
          }}
        >
          {copied ? "✓" : "⧉"}
        </button>
      </span>
    </Tooltip>
  );
};

const ALL_COLUMNS = [
  {
    key: "_id",
    title: "ID",
    defaultVisible: true,
    render: (r) => <TxIdCell id={r?._id} />,
  },
  {
    key: "createdAt",
    title: "Date",
    defaultVisible: true,
    render: (r) => formatDateTime(r?.createdAt),
  },
  {
    key: "user",
    title: "User",
    defaultVisible: true,
    render: (r) => {
      const u = r?.userDetails;
      if (!u) return "-";
      const fullName = [u?.firstName, u?.lastName].filter(Boolean).join(" ");
      const content = (
        <div>
          <div style={{ fontWeight: 500 }}>{fullName || "-"}</div>
          <div style={{ fontSize: 11, color: "#888" }}>
            {u?.email || u?.phoneNumber || "-"}
          </div>
        </div>
      );
      return u?._id ? (
        <Link
          to={`/admin/user/profile/${u._id}`}
          style={{ color: "inherit", textDecoration: "none" }}
          title="View user profile"
        >
          {content}
        </Link>
      ) : (
        content
      );
    },
  },
  {
    key: "coinSymbol",
    title: "Coin",
    defaultVisible: true,
    render: (r) =>
      r?.coinSymbol ? (
        <span style={{ fontWeight: 600 }}>{r.coinSymbol}</span>
      ) : (
        "-"
      ),
  },
  {
    key: "amount",
    title: "Amount",
    defaultVisible: true,
    align: "right",
    render: (r) => (
      <span style={{ fontFamily: "monospace", fontWeight: 500 }}>
        {formatAmount(r?.amount)}
      </span>
    ),
  },
  {
    key: "type",
    title: "Type",
    defaultVisible: true,
    render: (r) =>
      r?.type === "CREDIT" ? (
        <Tag color="green">CREDIT</Tag>
      ) : r?.type === "DEBIT" ? (
        <Tag color="red">DEBIT</Tag>
      ) : (
        "-"
      ),
  },
  {
    key: "status",
    title: "Status",
    defaultVisible: true,
    render: (r) => {
      const isTrmPending = r?.status === "PENDING" && r?.holdSourceTag === "TRM_PROCESSING";
      if (isTrmPending) {
        return (
          <Tooltip title="Awaiting TRM async screening — will auto-proceed once TRM finishes">
            <Tag color="processing">TRM Processing</Tag>
          </Tooltip>
        );
      }
      const cfg = STATUS_TAG[r?.status];
      return cfg ? <Tag color={cfg.color}>{cfg.label}</Tag> : <Tag>{r?.status || "-"}</Tag>;
    },
  },
  {
    key: "reason",
    title: "Reason",
    defaultVisible: true,
    render: (r) => {
      const msg = r?.errorMessage || r?.holdReason;
      if (!msg) return "-";
      return (
        <Tooltip title={msg}>
          <span style={{ display: "inline-block", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom", color: r?.status === "FAILED" ? "#cf1322" : "#d46b08" }}>
            {msg}
          </span>
        </Tooltip>
      );
    },
  },
  {
    key: "txHash",
    title: "Tx Hash",
    defaultVisible: true,
    render: (r) => <TxIdCell id={r?.txHash} />,
  },
  {
    key: "chain",
    title: "Chain",
    defaultVisible: false,
    render: (r) => r?.chain || "-",
  },
  {
    key: "toAddress",
    title: "To Address",
    defaultVisible: false,
    render: (r) => <TxIdCell id={r?.toAddress} />,
  },
  {
    key: "fromAddress",
    title: "From Address",
    defaultVisible: false,
    render: (r) => <TxIdCell id={r?.fromAddress} />,
  },
];

const FundingWalletTransactions = () => {
  const history = useHistory();
  const [list, setList] = useState({ result: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // filter state
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [coinSymbol, setCoinSymbol] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "finance-funding-wallet-tx",
    allColumns: ALL_COLUMNS,
  });

  // Debounce the search field — fire API 400ms after user stops typing.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo(() => {
    const p = { page, sizePerPage: pageSize };
    if (searchTerm) p.search = searchTerm;
    if (userEmail)  p.userEmail = userEmail;
    if (coinSymbol) p.coinSymbol = coinSymbol;
    if (type)       p.type = type;
    if (status)     p.status = status;
    if (startDate)  p.startDate = startDate;
    if (endDate)    p.endDate = endDate;
    return p;
  }, [page, pageSize, searchTerm, userEmail, coinSymbol, type, status, startDate, endDate]);

  const getList = useCallback(() => {
    setLoading(true);
    getFundingWalletTransactionsApi(params)
      .then((res) => {
        if (res?.status) {
          setList({
            result: res?.data?.result || [],
            total: res?.data?.total || 0,
          });
        } else {
          toast.error(res?.message || "Failed to load transactions", { toastId: "funding-wallet-fetch-error" });
        }
      })
      .catch((err) => {
        const msg =
          err?.data?.message ||
          err?.response?.data?.message ||
          "Failed to load transactions";
        toast.error(msg, { toastId: "funding-wallet-fetch-error" });
      })
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    getList();
  }, [getList]);

  // Reset to page 1 whenever any filter / size changes
  useEffect(() => {
    setPage(1);
  }, [pageSize, searchTerm, userEmail, coinSymbol, type, status, startDate, endDate]);

  const handleDateRangeChange = (event, picker) => {
    setStartDate(picker.startDate.format("YYYY-MM-DD"));
    setEndDate(picker.endDate.format("YYYY-MM-DD"));
  };

  const handleClear = () => {
    setSearchInput("");
    setSearchTerm("");
    setUserEmail("");
    setCoinSymbol("");
    setType("");
    setStatus("");
    setStartDate();
    setEndDate();
  };

  const handleExport = () => {
    if (exporting) return;
    setExporting(true);
    const exportParams = { ...params };
    delete exportParams.page;
    delete exportParams.sizePerPage;
    exportFundingWalletTransactionsCsvApi(exportParams)
      .then((res) => {
        const fname = `crypto-wallet-transactions-${moment().format("YYYYMMDD")}.csv`;
        saveBlobAsFile(res, fname);
      })
      .catch((err) => {
        const msg =
          err?.data?.message ||
          err?.response?.data?.message ||
          "Failed to export CSV";
        toast.error(msg);
      })
      .finally(() => setExporting(false));
  };

  const columns = [...dynamicColumns];

  return (
    <>
      <style>{`
        .row-on-hold td { background: #fffbe6 !important; }
        .row-trm-pending td { background: #fff7e6 !important; }
        .ant-table-row:hover td { background: #f0f5ff !important; }
      `}</style>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header py-3 mb-2">
            <div className="row align-items-center">
              <div className="col-sm-12">
                <h3 className="page-title mb-1">Crypto Wallet Transactions</h3>
                <ul className="breadcrumb mb-0" style={{ fontSize: 13 }}>
                  <li className="breadcrumb-item">
                    <Link to="/admin" className="text-decoration-none">
                      Dashboard
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Finance Dashboard / Crypto Wallet Transactions
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">
              <div className="card" style={{ borderRadius: 12, border: "1px solid #e9ecef", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                <div className="card-body">
                  <TableToolbar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    total={list.total}
                    page={page}
                    visibleColumnKeys={visibleColumnKeys}
                    allColumns={ALL_COLUMNS}
                    toggleColumn={toggleColumn}
                    resetColumns={resetColumns}
                    onRefresh={getList}
                    refreshLoading={loading}
                    rightSlot={
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleExport}
                        disabled={exporting || !list.total}
                        title={
                          !list.total
                            ? "No data to export"
                            : "Export filtered results as CSV"
                        }
                      >
                        {exporting ? <Spinner size="sm" /> : "Export CSV"}
                      </button>
                    }
                  />
                  <div className="table-responsive">
                    {loading ? (
                      <div className="his-spin">
                        <Spin
                          size="large"
                          style={{ position: "relative", top: "35%" }}
                        />
                      </div>
                    ) : (
                      <Table
                        pagination={false}
                        style={{ overflowX: "auto" }}
                        columns={columns}
                        dataSource={list.result}
                        rowKey={(record) => record?._id || record?.txHash}
                        onRow={(record) => ({
                          onClick: () => record?._id && history.push(`/admin/finance/funding-wallet-transactions/${record._id}`),
                          style: { cursor: "pointer" },
                        })}
                        rowClassName={(record) =>
                          record?.status === "ON_HOLD" ? "row-on-hold" :
                          (record?.status === "PENDING" && record?.holdSourceTag === "TRM_PROCESSING") ? "row-trm-pending" : ""
                        }
                        locale={{
                          emptyText: (
                            <div style={{ padding: "32px 0", color: "#888" }}>
                              No transactions found
                            </div>
                          ),
                        }}
                      />
                    )}
                    {list.total > pageSize && (
                      <CustomPagination
                        key={`pg-${pageSize}`}
                        totalTabs={list.total}
                        tabsPerPage={pageSize}
                        handleChange={setPage}
                        className={loading ? "display-hide" : "display-flex"}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="list-layout__filters">
              <FilterPanel count={list.total} onClear={handleClear}>
                <FilterPanel.Field label="Search">
                  <input
                    type="text"
                    placeholder="Tx hash, Fireblocks ID, name, email, phone"
                    value={searchInput}
                    onChange={(e) =>
                      setSearchInput(sanitizeInput(e.target.value))
                    }
                  />
                </FilterPanel.Field>

                <FilterPanel.Field label="User Email">
                  <input
                    type="text"
                    placeholder="user@example.com"
                    value={userEmail}
                    onChange={(e) => setUserEmail(sanitizeInput(e.target.value))}
                  />
                </FilterPanel.Field>

                <FilterPanel.Field label="Coin Symbol">
                  <input
                    type="text"
                    placeholder="e.g. BTC, ETH, USDT"
                    value={coinSymbol}
                    onChange={(e) =>
                      setCoinSymbol(sanitizeInput(e.target.value).toUpperCase())
                    }
                  />
                </FilterPanel.Field>

                <FilterPanel.Field label="Type">
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value || "all"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </FilterPanel.Field>

                <FilterPanel.Field label="Status">
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value || "all"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </FilterPanel.Field>

                <FilterPanel.Field label="Date Range">
                  <DateRangePicker
                    onApply={handleDateRangeChange}
                    isClearable={true}
                  >
                    <input
                      type="text"
                      placeholder="Select date range"
                      value={
                        startDate && endDate
                          ? `${startDate} → ${endDate}`
                          : ""
                      }
                      readOnly
                    />
                  </DateRangePicker>
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FundingWalletTransactions;
